import { type FormEvent, useEffect, useState } from 'react';
import { i18n, wordCount } from '@wms/shared';
import { api } from '../lib/api.js';
import { Dialog } from './Dialog.js';
import { NumberInput } from './NumberInput.js';
import { SearchSelect } from './SearchSelect.js';
import { ReceiptPalletPicker } from './ReceiptPalletPicker.js';

const { t } = i18n;

interface SkuOpt {
  id: string;
  code: string;
  type: 'PRODUCT' | 'COMPONENT';
  nameLt: string;
  photoUrl: string | null;
  availableQty: number;
  maxProducibleQty: number | null;
}

interface Line {
  skuId: string;
  qty: number;
  comment: string;
}

export function RestockDialog({
  partnerId,
  onClose,
  onRecorded,
}: {
  partnerId: string;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const [skus, setSkus] = useState<SkuOpt[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [placement, setPlacement] = useState('direct');
  const [palletId, setPalletId] = useState('');

  useEffect(() => {
    // Both components and products can be received directly into stock
    // (Jonas #6.1; was COMPONENT-only).
    api<SkuOpt[]>(`/partners/${partnerId}/skus`)
      .then((rows) => {
        setSkus(rows);
        if (rows.length > 0) setLines([{ skuId: '', qty: 1, comment: '' }]);
      })
      .catch(() => setSkus([]));
  }, [partnerId]);

  function addLine() {
    if (skus.length === 0) return;
    if (lines.some((line) => !line.skuId)) return;
    setLines((d) => [...d, { skuId: '', qty: 1, comment: '' }]);
  }

  function removeLine(i: number) {
    setLines((d) => d.filter((_, j) => j !== i));
  }

  function patchLine(i: number, patch: Partial<Line>) {
    setLines((d) => d.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  }

  const valid =
    lines.length > 0 &&
    lines.every((l) => l.skuId && l.qty > 0 && wordCount(l.comment) >= 2) &&
    (placement === 'direct' || Boolean(palletId));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!valid) {
      setError(t('stock.restock.comment_too_short'));
      return;
    }
    setBusy(true);
    try {
      await api(`/partners/${partnerId}/stock/restock`, {
        method: 'POST',
        body: {
          ...(placement === 'pallet' ? { palletId } : {}),
          lines: lines.map((l) => ({
            skuId: l.skuId,
            qty: l.qty,
            comment: l.comment,
          })),
        },
      });
      onRecorded();
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('stock.restock.title')} onClose={onClose}>
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="space-y-3 text-sm max-h-[70vh] overflow-y-auto pr-1"
      >
        {skus.length === 0 ? (
          <div className="rounded border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
            {t('stock.restock.empty')}
          </div>
        ) : (
          <>
            <label className="block space-y-1">
              <span className="font-medium">{t('stock.restock.placement')}</span>
              <select
                value={placement}
                onChange={(event) => {
                  setPlacement(event.target.value);
                  setPalletId('');
                }}
                className="w-full rounded border border-border bg-background px-2 py-1"
              >
                <option value="direct">{t('stock.restock.direct')}</option>
                <option value="pallet">{t('stock.restock.existing_pallet')}</option>
              </select>
            </label>
            {placement === 'pallet' ? (
              <ReceiptPalletPicker
                key={partnerId}
                partnerId={partnerId}
                value={palletId}
                onChange={setPalletId}
              />
            ) : null}
            <ul className="space-y-2">
              {lines.map((line, i) => (
                <li key={i} className="space-y-2 rounded border border-border p-2">
                  <div className="flex items-center gap-2">
                    <SearchSelect
                      className="min-w-0 flex-1 text-sm"
                      ariaLabel={t('bom.pick_component')}
                      value={line.skuId}
                      onChange={(id) => patchLine(i, { skuId: id })}
                      options={skus.map((s) => ({
                        id: s.id,
                        label: `${s.code} — ${s.nameLt} (${t(`sku.type.${s.type}`)})`,
                        imageUrl: s.photoUrl,
                        availableQty: s.availableQty,
                        maxProducibleQty: s.maxProducibleQty ?? undefined,
                      }))}
                    />
                    <NumberInput
                      integer
                      min={1}
                      value={line.qty}
                      onChange={(v) => patchLine(i, { qty: v ?? 0 })}
                      required
                      className="w-24 rounded border border-border bg-background px-2 py-1 text-sm tabular-nums"
                    />
                    <button
                      type="button"
                      onClick={() => removeLine(i)}
                      disabled={lines.length <= 1}
                      aria-label={t('common.delete')}
                      className="rounded border border-border px-2 py-1 text-sm hover:bg-muted disabled:opacity-40"
                    >
                      ✕
                    </button>
                  </div>
                  <label className="block space-y-1">
                    <span className="font-medium">{t('stock.restock.comment')}</span>
                    <textarea
                      value={line.comment}
                      onChange={(e) => patchLine(i, { comment: e.target.value })}
                      required
                      rows={2}
                      placeholder={t('stock.restock.comment_hint')}
                      className="w-full rounded border border-border bg-background px-2 py-1"
                    />
                  </label>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={addLine}
              disabled={lines.some((line) => !line.skuId)}
              className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              + {t('stock.restock.add_line')}
            </button>
          </>
        )}

        {error ? <div className="text-red-600">{error}</div> : null}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !valid}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('stock.restock.submit')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
